import blue from "@material-ui/core/colors/blue";
import blueBadge from "assets/blue-badge.svg";
import diamondBadge from "assets/diamond-badge.svg";
import goldBadge from "assets/gold-badge.svg";
import compact from "lodash/compact";
import isString from "lodash/isString";
import { DateTime, Interval } from "luxon";
import moment from "moment";
import qs from "query-string";

import { fbTsToDate } from "common/date";
import { isValidPhoneNumber } from "common/forms";
import { ORDER_TYPE } from "util/constants";

import { INITIATOR } from "./constants";
import flattenMap from "./flattenMap";
import loadStyle from "./loadStyle";
import paramsToQs from "./paramsToQs";

export { flattenMap, loadStyle, paramsToQs };

export function getTableNumber(tableArr) {
  const tables = [];
  tableArr.map((table) => tables.push(table?.tableNumber));
  return compact(tables);
}

export function getActiveBlockes(blockArr) {
  const currentDate = new Date();
  const endManually = blockArr.filter((block) => !block?.endDate);
  const haveEndDates = blockArr.filter(
    (block) => fbTsToDate(block.endDate) > currentDate
  );
  const allBlocks = [...endManually, ...haveEndDates];
  return allBlocks;
}

export function findBlockedTableNumbers(allTables, blockData) {
  let selectedTableUids = [];
  let blockedtables = [];
  let tableBlocks = [];
  blockedtables = flattenMap(blockData);
  tableBlocks = getActiveBlockes(blockedtables);
  tableBlocks.map((block) => selectedTableUids.push(...(block?.tables ?? [])));

  const selectedTables = selectedTableUids.map((uid) =>
    allTables.find((table) => table.uid === uid)
  );

  return getTableNumber(selectedTables);
}

export function findCommonBlockedTables(blockedTablesArr, selectedTables) {
  let hasCommontables = false;
  const commons = blockedTablesArr.some((table) =>
    selectedTables.includes(table)
  );
  if (commons) hasCommontables = true;
  return hasCommontables;
}

export function timeWaiting(visit) {
  if (
    !visit.hasOwnProperty("arrivalTime") ||
    !visit.hasOwnProperty("processedTime")
  )
    return 0;
  const arrival = fbTsToDate(visit.arrivalTime);
  const processed = fbTsToDate(visit.processedTime);
  const minWaiting = (processed - arrival) / (1000 * 60);

  return minWaiting;
}

export function getTimeRange(minutes, businessUid) {
  if (minutes === 0) {
    return <span>No Wait!</span>;
  } else if (minutes > 0 && minutes <= 5) {
    return <span>0-5 min</span>;
  } else if (minutes > 5 && minutes <= 10) {
    return <span>5-10 min</span>;
  } else if (minutes > 10 && minutes <= 15) {
    return <span>10-15 min</span>;
  } else if (minutes > 15 && minutes <= 20) {
    return <span>15-20 min</span>;
  } else if (minutes > 20 && minutes <= 25) {
    return <span>20-25 min</span>;
  } else if (minutes > 25 && minutes <= 30) {
    return <span>25-30 min</span>;
  } else if (minutes > 30 && minutes <= 35) {
    return <span>30-35 min</span>;
  } else if (minutes > 35 && minutes <= 40) {
    return <span>35-40 min</span>;
  } else if (minutes > 40 && minutes <= 45) {
    return <span>40-45 min</span>;
  } else if (minutes > 45 && minutes <= 50) {
    return <span>45-50 min</span>;
  } else if (minutes > 50 && minutes <= 55) {
    return <span>50-55 min</span>;
  } else if (minutes > 55 && minutes <= 60) {
    return <span>55-60 min</span>;
  } else if (minutes > 60 && minutes <= 65) {
    return <span>60-65 min</span>;
  } else if (minutes > 65 && minutes <= 70) {
    return <span>65-70 min</span>;
  } else if (minutes > 70 && minutes <= 75) {
    return <span>70-75 min</span>;
  } else {
    if (businessUid === "lazycat-riyadh") return <span>75-240 min</span>;
    return <span>75+ min</span>;
  }
}

export function msToTime(duration) {
  let seconds = Math.floor((duration / 1000) % 60);
  let minutes = Math.floor((duration / (1000 * 60)) % 60);
  let hours = Math.floor((duration / (1000 * 60 * 60)) % 24);

  hours = hours < 10 ? "0" + hours : hours;
  minutes = minutes < 10 ? "0" + minutes : minutes;
  seconds = seconds < 10 ? "0" + seconds : seconds;

  return (hours > 0 ? hours + ":" : "") + minutes + ":" + seconds;
}

export const ellipsisStyle = (width) => ({
  width,
  maxWidth: width,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

export const getBadge = (count) => {
  if (!count) return null;

  if (count > 9) return diamondBadge;
  if (count > 5) return goldBadge;
  if (count > 2) return blueBadge;

  return null;
};

export const getElapsedTimeStr = (from) => {
  const time = new Date();
  const duration = time - from;
  const tooLong = duration / (1000 * 60 * 60 * 24) > 1;
  const str = msToTime(duration);
  return tooLong ? "∞" : str;
};

export const isDateEqual = (date1, date2) => {
  if (
    !date1 ||
    !date2 ||
    typeof date1.getDate !== "function" ||
    typeof date2.getDate !== "function"
  )
    return false;
  return (
    date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getYear() === date2.getYear()
  );
};

export const capitalizeWord = (text) => {
  if (!text) return "";
  let capitalized = text.toLowerCase();
  return capitalized.charAt(0).toUpperCase() + capitalized.slice(1);
};

export const getStartEndDate = (dateRange) => {
  let start;
  let end = moment();

  switch (dateRange) {
    case "Yesterday": {
      start = moment()
        .set("date", moment().date() - 1)
        .startOf("day");
      end = moment(start).endOf("day");
      break;
    }
    case "Last 7 Days": {
      start = moment()
        .set("date", moment().date() - 7)
        .startOf("day");
      break;
    }
    case "Last 30 Days": {
      start = moment()
        .set("date", moment().date() - 30)
        .startOf("day");
      break;
    }
    case "Last Year": {
      start = moment()
        .set("date", moment().date() - 365)
        .startOf("day");
      break;
    }
    case "Custom":
    default:
      return;
  }
  return { start: start.toDate(), end: end.toDate() };
};

export const setUrlParams = (location, history, params) => {
  let query = qs.parse(location.search, { ignoreQueryPrefix: true });
  query = { ...query, ...params };
  const stringified = qs.stringify(query);
  history.push(history.location.pathname + "?" + stringified);
};

export const getSearchSpans = (searchTerm = "", str = "", style, className) => {
  function chunkString(searchStr, str, caseSensitive) {
    var searchStrLen = searchStr.length;
    if (searchStrLen === 0) {
      return [str];
    }
    var index = 0,
      chunks = [];
    if (!caseSensitive) {
      searchStr = searchStr.toLowerCase();
    }
    while ((index = str?.toLowerCase().indexOf(searchStr)) > -1) {
      const endIdx = searchStr.length + index;
      chunks.push(str.slice(0, index));
      chunks.push(str.slice(index, endIdx));
      str = str.slice(index + searchStr.length);
    }
    chunks.push(str);
    return chunks;
  }

  let chunks = chunkString(searchTerm, str);

  const retVal = chunks.map((chunk, index) => {
    if (chunk?.toLowerCase?.() === searchTerm.toLowerCase()) {
      return (
        <span
          key={index}
          className={className}
          style={{ ...style, color: blue[300], fontWeight: 400 }}
        >
          {chunk}
        </span>
      );
    } else if (!isString(chunk)) {
      return "";
    } else {
      return (
        <span key={index} className={className} style={style}>
          {chunk}
        </span>
      );
    }
  });
  return retVal;
};

export function roundHalf(num) {
  return Math.round(num * 2) / 2;
}

export function ceil(value, step) {
  step || (step = 1.0);
  var inv = 1.0 / step;
  return Math.ceil(value * inv) / inv;
}

export function floor(value, step) {
  step || (step = 1.0);
  var inv = 1.0 / step;
  return Math.floor(value * inv) / inv;
}

export function roundToTwoDecimal(num) {
  if (isNaN(num)) return;
  return Number(num).toFixed(2);
}

export function createGmapsOverlays(map, maps, business, overlayStyle) {
  if (!maps) return;

  // eslint-disable-next-line consistent-return
  return business?.services?.[ORDER_TYPE.DELIVERY]?.zones?.map((zone) => {
    const { type, minAmount, fee, ...others } = zone;
    const data = { ...others, ...(map && { map }), ...overlayStyle };
    const objBase = { type, minAmount, fee };
    if (zone.type === "circle") {
      return {
        ...objBase,
        overlay: new maps.Circle(data),
      };
    }
    if (zone.type === "rectangle") {
      return {
        ...objBase,
        overlay: new maps.Rectangle(data),
      };
    }
    if (zone.type === "polygon") {
      return {
        ...objBase,
        overlay: new maps.Polygon(data),
      };
    }
  });
}

export function shuffleArray(array) {
  const newArr = [...array];
  for (let i = newArr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArr[i], newArr[j]] = [newArr[j], newArr[i]];
  }
  return newArr;
}

function getNext7Days() {
  const next7days = {};
  const curr = moment().subtract(1, "days");
  for (let i = -1; i < 6; i++) {
    const day = curr.format("dddd");
    next7days[day] = i;
    curr.add(1, "days");
  }
  return next7days;
}

const getHoursAndMinutes = (time, prop) => {
  if (!time) {
    if (prop === "start") return { hours: 0, minutes: 0 };
    if (prop === "end") return { hours: 23, minutes: 59 };
  }

  let hours;
  let minutes = 0;
  if (typeof time === "number") {
    hours = time;
  } else {
    [hours, minutes] = time.split(":").map(Number);
  }
  return { hours, minutes };
};

export function getSchedulePeriods(schedule) {
  function mapPeriod(period) {
    const next7days = getNext7Days();

    const now = new moment()
      .milliseconds(0)
      .seconds(0)
      .minutes(0);
    const day = moment(now).add(next7days[period.day], "days");
    let start;
    const hasStart =
      period.hasOwnProperty("startTime") && period.hasOwnProperty("openDay");
    if (hasStart) {
      const { hours, minutes } = getHoursAndMinutes(period?.startTime, "start");
      start = moment(now)
        .hours(hours)
        .minutes(minutes)
        .add(next7days[period.openDay], "days");
    }
    let end;
    const hasEnd =
      period.hasOwnProperty("endTime") && period.hasOwnProperty("closeDay");
    if (hasEnd) {
      const { hours, minutes } = getHoursAndMinutes(period?.endTime, "end");
      end = moment(now)
        .hours(hours)
        .minutes(minutes)
        .add(next7days[period.closeDay], "days");
    }
    if (period?.allDay) {
      start = moment(day).startOf("day");
      end = moment(day)
        .add(1, "days")
        .startOf("day");
    }

    return {
      ...period,
      start,
      end,
      day,
    };
  }

  if (!schedule) return [];

  let periods = [];
  let newSchedule = Object.entries(schedule).map((entry) => {
    const [day, arr] = entry;
    const newArr = arr.map((item) => ({ ...item, day }));
    return newArr;
  });
  newSchedule.forEach((arr) => {
    if (typeof arr[Symbol.iterator] === "function") {
      periods.push(...arr);
    }
  });
  periods = periods.map(mapPeriod);
  periods.sort((a, b) => {
    if (a.day > b.day) return 1;
    if (a.day < b.day) return -1;
    return a.start > b.start;
  });
  return periods;
}

export function getPeriods(momentDate, schedule) {
  // Flatten all the time periods for the week
  let periods = getSchedulePeriods(schedule);
  // Find time periods that start or end on today (time range could have wrapped around from the previous day)
  // Create a moment from the start and end times and dates. We need this to compare across date boundaries
  const day = moment(momentDate).format("dddd");
  periods = periods.filter(
    (period) => period.openDay === day || period.closeDay === day
  );
  return periods;
}

export function isWithinSchedule(momentDate, periods) {
  if (!periods) return true;

  const day = moment(momentDate).format("dddd");
  // Check if this period is open all day
  const dayPeriod = periods.find((period) => period.day.format("dddd") === day);
  if (dayPeriod && dayPeriod.allDay) return true;

  const filterPeriods = periods.filter(
    (period) => period.openDay === day || period.closeDay === day
  );

  return filterPeriods?.some?.((period) =>
    momentDate.isBetween(period.start, period.end)
  );
}

export function isWithinDates(momentDate, startDate, endDate) {
  if (!(startDate && endDate)) return true;
  if (
    moment(momentDate).isBetween(
      moment(startDate),
      moment(endDate),
      "day",
      "[]"
    )
  ) {
    return true;
  } else {
    return false;
  }
}

export function isPossiblePhoneNumber(number) {
  if (number.startsWith("+")) {
    return isValidPhoneNumber(number);
  } else {
    return isValidPhoneNumber("+" + number);
  }
}

export function arrayMoveMutate(array, from, to) {
  const startIndex = to < 0 ? array.length + to : to;
  const item = array.splice(from, 1)[0];
  array.splice(startIndex, 0, item);
}

export function arrayMove(array, from, to) {
  array = array.slice();
  arrayMoveMutate(array, from, to);
  return array;
}

const INITIAL_DATE_TIME = {
  hour: 0,
  minute: 0,
  second: 0,
  millisecond: 0,
};

export function getSlots(startTime, endTime, interval = 30, day = new Date()) {
  const startOfDay = DateTime.fromJSDate(day);
  let start = startOfDay.set({
    ...INITIAL_DATE_TIME,
    hour: startTime,
  });
  const end = startOfDay.set({
    ...INITIAL_DATE_TIME,
    hour: endTime < startTime ? endTime + 24 : endTime,
  });
  const slots = [];
  while (start < end) {
    const slotEnd = start.plus({ minute: interval });
    slots.push({ start, end: slotEnd, available: true });
    start = slotEnd;
  }
  return slots;
}

const checkIfEndEqualsStart = (start, end) => {
  const startDateTime = start.set({ second: 0, millisecond: 0 });
  const endDateTime = end.set({ second: 0, millisecond: 0 });
  return +startDateTime === +endDateTime;
};
export function mapPax(reservations, slot, durationOfBooking = 60) {
  const slotStart = slot.start;
  const slotEnd = slot.end;
  const slotInterval = Interval.fromDateTimes(slotStart, slotEnd);
  const currentPax = reservations.reduce((total, reservation) => {
    const duration = reservation.durationOfBooking ?? durationOfBooking;
    const reservationDateTime = DateTime.fromJSDate(
      fbTsToDate(reservation.dateTime)
    );
    const reservationEnd = reservationDateTime.plus({ minutes: duration });
    const reservationInterval = Interval.fromDateTimes(
      reservationDateTime,
      reservationEnd
    );
    const overlaps =
      slotInterval.overlaps(reservationInterval) &&
      !checkIfEndEqualsStart(slotStart, reservationEnd);

    if (overlaps) return total + Number(reservation.partySize ?? 0);

    return total;
  }, 0);

  return { ...slot, currentPax };
}

export function getAddress(address) {
  let strNumber = "";
  let strName = "";
  let city = "";
  let flat = address?.flat ? `${address.flat}` : "";
  let area = address?.area ? `${address.area},` : "";
  let building = address?.building ? `${address.building},` : "";
  const directions =
    address?.directions !== null &&
    address?.directions !== "" &&
    address?.directions !== undefined
      ? `Directions: ${address.directions}`
      : "";

  if (address?.place) {
    let arr = address.place?.address_components ?? [];
    arr.forEach((el) => {
      if (el.types[0] === "street_number") {
        strNumber = `${el.long_name},`;
      } else if (el.types[0] === "route") {
        strName = el.long_name;
      } else if (el.types[0] === "administrative_area_level_1") {
        city = el.long_name;
      } else {
        return null;
      }
    });
  }

  const addressFull = {
    flat: `${flat}`,
    building: `${building}`,
    streetShort: `${strNumber} ${strName}`,
    street: `${flat} ${building} ${strNumber} ${strName}`,
    area: `${area} ${city}`,
    origArea: `${area}`,
    city: `${city}`,
    directions,
    str: `${flat} ${building} ${strNumber} ${strName} (${area} ${city})`,
    bigStr: `${flat} ${building} ${strNumber} ${strName} (${area} ${city}) ${directions}`,
  };
  return addressFull;
}

export function addPlus(number) {
  // if (!number || typeof number.startsWith !== "function") return number;
  if (!number?.startsWith("+")) return "+" + number;
  return number;
}

function combinePeriods(periods) {
  if (periods.length === 0) return [];
  const combinedPeriods = [];
  let currentPeriod = periods[0];
  for (let i = 1; i < periods.length; i++) {
    const period = periods[i];
    const currentPeriodEnd = currentPeriod.end;
    const periodStart = period.start;
    const currentPeriodEndPlusOne = moment(currentPeriodEnd).add(1, "minute");
    const periodStartMinusOne = moment(periodStart).subtract(1, "minute");
    const isOverlapping =
      currentPeriodEndPlusOne.isSameOrAfter(periodStart) ||
      currentPeriodEndPlusOne.isSame(periodStartMinusOne);
    if (isOverlapping) {
      currentPeriod.end = period.end;
    } else {
      combinedPeriods.push(currentPeriod);
      currentPeriod = period;
    }
  }
  combinedPeriods.push(currentPeriod);
  return combinedPeriods;
}

export function getNextBound(periodsBase) {
  if (!periodsBase) return null;
  const periods = combinePeriods(periodsBase);
  // If every period is open allDay, we're always open
  if (periods.every((period) => period.allDay)) return null;

  const now = moment();
  for (let i = 0; i < periods.length; i++) {
    // If current period is marked as all day and next period is marked as all day and next period is tomorrow, continue
    let period = periods[i];
    const periodDay = period.day.days();
    let nextPeriod = periods[i + 1];
    const nextPeriodDay = nextPeriod?.day?.days?.();
    let isNextPeriodTomorrow = (periodDay + 1) % 7 === nextPeriodDay;
    if (period.allDay && nextPeriod?.allDay && isNextPeriodTomorrow) continue;

    const isOpen = now.isBetween(period.start, period.end);
    if (isOpen) return period.end;
    if (now < period.start) return period.start;
  }

  return null;
}

export function getLastUpdate(auth, email, initiator) {
  const source =
    initiator === "restaurant"
      ? INITIATOR.RESTAURANT
      : initiator === "customer"
      ? INITIATOR.CUSTOMER
      : INITIATOR.CUSTOMER;

  return {
    auth,
    email,
    source,
    time: new Date(),
  };
}
const hasArabic = (str) => {
  const arabicRegex = /[\u0600-\u06FF]/;
  return arabicRegex.test(str);
};

const containsFrenchLetters = (text) => {
  // Define a regular expression pattern to match specific French letters
  const frenchLettersPattern = /[ÀàÂâÇçÉéÈèÊêËëÎîÏïÔôŒœÙùÛûÜü]/;

  for (let i = 0; i < text.length; i++) {
    if (frenchLettersPattern.test(text[i])) {
      return true; // Found a French letter
    }
  }

  return false; // No French letters found
};

export const getSmsCount = (sms) => {
  if (!sms || !sms.body || typeof sms.body !== "string" || !sms.body.length) {
    return 1;
  }

  const isNonEnglish = hasArabic(sms) || containsFrenchLetters(sms);
  //check on http://sms.bulk-sms-cloud.me/API.aspx for more details on sms length calculation
  const doubleSMSminimum = isNonEnglish ? 71 : 161;
  const doubleSMSmaximum = isNonEnglish ? 135 : 305;
  const longSMScount = isNonEnglish ? 67 : 144;

  if (sms.body.length < doubleSMSminimum) {
    return 1;
  }
  if (
    sms.body.length >= doubleSMSminimum &&
    sms.body.length < doubleSMSmaximum
  ) {
    return 2;
  }
  if (sms.body.length >= doubleSMSmaximum) {
    const length = sms.body.length - doubleSMSmaximum;
    return 3 + Math.floor(length / longSMScount);
  }
  return 1;
};

export function numberWithCommas(num) {
  var num_parts = num.toString().split(".");
  num_parts[0] = num_parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return num_parts.join(".");
}

export function getTableNumString(tableNumber) {
  if (isNaN(tableNumber)) {
    return tableNumber;
  } else {
    return `T${tableNumber}`;
  }
}

export function descDate(a, b, timeProp = "dateTime") {
  const dateA = fbTsToDate(a[timeProp]);
  const dateB = fbTsToDate(b[timeProp]);
  if (dateA > dateB) return -1;
  if (dateA < dateB) return 1;
  return 0;
}

export function ascDate(a, b, timeProp = "dateTime") {
  const dateA = fbTsToDate(a[timeProp]);
  const dateB = fbTsToDate(b[timeProp]);
  if (dateA > dateB) return 1;
  if (dateA < dateB) return -1;
  return 0;
}

export async function loadScript(src) {
  return new Promise((resolve, reject) => {
    let script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.defer = true;
    script.crossOrigin = "anonymous";
    script.addEventListener("load", function() {
      resolve();
    });
    script.addEventListener("error", function(e) {
      reject(e);
    });
    document.body.appendChild(script);
  });
}

export const getUniqueIncrementedName = (name, type, existingNames) => {
  let baseName = name;
  let suffix = 0;

  const namePattern = /^(.*?)(_\((\d+)\))?(\.[^.]+)?$/;

  const match = name.match(namePattern);

  if (match) {
    baseName = match[1];
    suffix = match[3] ? parseInt(match[3]) : 0;
  }

  let newName = name;

  do {
    suffix++;
    if (type === "file" && match[4]) {
      newName = `${baseName}_(${suffix})${match[4]}`;
    } else {
      newName = `${baseName}_(${suffix})`;
    }
  } while (existingNames.includes(newName));

  return newName;
};

export const getBusinessTimeZone = (business) => {
  const businessTimeZone =
    business.hasOwnProperty("settings") &&
    business.settings.hasOwnProperty("timeZone")
      ? business.settings.timeZone
      : "Asia/Dubai";

  return businessTimeZone;
};

export const checkIfVideo = (url = "") =>
  url?.includes(".mp4") || url?.includes(".webm") || url?.includes("video");
